import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, X, ArrowRight, ShieldCheck, Lock, KeyRound, DatabaseBackup, FileCheck, GraduationCap, Target, HeartHandshake, Store, TrendingUp, School, Building2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProductMockup from "@/components/marketing/ProductMockup";
import { track } from "@/lib/api";
import { useSEO, ORG_SCHEMA, WEBSITE_SCHEMA, SOFTWARE_SCHEMA } from "@/lib/seo";
import { useSite } from "@/context/SiteContext";
import { HOME_DEFAULTS } from "@/lib/homeDefaults";
import { MODULES, MODULE_LINKS, PROBLEMS, BENEFITS, BEFORE_AFTER, SEGMENTS } from "@/lib/content";

const ICONS = { GraduationCap, Target, HeartHandshake, Store, TrendingUp, School, Building2, Users };
const SEGMENT_PAGES = ["training-organizations", "ngos", "small-businesses", "educational-institutes", "multi-center-organizations"];
const DEMO_TABS = ["dashboard", "finance", "hr", "attendance", "payroll", "expenses", "invoices", "reports"];

export default function Home() {
  const navigate = useNavigate();
  const { cms: siteCms, site } = useSite();
  const cms = siteCms || { settings: {}, faqs: [], testimonials: [] };
  const t = { ...HOME_DEFAULTS, ...(site.home || {}) };
  const [demoTab, setDemoTab] = useState("dashboard");

  useSEO({
    title: "All-in-One Finance, HRMS & Operations Software | Mashara Finance",
    description: "Mashara Finance is an all-in-one business management platform for Finance, Accounting, HRMS, Payroll, Attendance, Partner Settlement, Candidate Management, Operations and Role-Based Access Control.",
    path: "/",
    jsonLd: [ORG_SCHEMA, WEBSITE_SCHEMA, SOFTWARE_SCHEMA],
  });

  const trustItems = cms.settings?.trust_items || [];
  const faqs = (cms.faqs || []).slice(0, 6);
  const testimonials = cms.testimonials || [];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="bg-gradient-to-b from-emerald-50/60 via-slate-50 to-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-24">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-4">{t.eyebrow}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-slate-900" data-testid="hero-headline">
              {t.h1}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
              {t.sub}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button data-testid="hero-start-trial-button" size="lg"
                onClick={() => { track("hero_cta_click", { location: "hero" }); navigate("/signup"); }}
                className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white px-8 py-6 text-base font-semibold">
                {t.cta_primary} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button data-testid="hero-see-how-button" size="lg" variant="outline"
                onClick={() => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" })}
                className="rounded-xl border-slate-300 px-8 py-6 text-base font-semibold text-slate-700 hover:text-emerald-700 hover:border-emerald-300">
                {t.cta_secondary}
              </Button>
            </div>
            <p className="mt-4 text-sm text-slate-600 font-medium">
              {t.trial_note}
            </p>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2" data-testid="hero-trust-badges">
              {t.trust_badges.split(",").map((x) => x.trim()).filter(Boolean).map((b) => (
                <span key={b} className="flex items-center gap-1.5 text-sm text-slate-600">
                  <Check className="w-4 h-4 text-emerald-600" /> {b}
                </span>
              ))}
            </div>
            <p className="mt-8 text-xs font-medium tracking-wide text-slate-500" data-testid="hero-module-chain">
              {t.module_chain}
            </p>
          </div>
          <div className="mt-14" data-testid="hero-product-visual">
            <ProductMockup active="dashboard" />
            <p className="text-center text-xs text-slate-500 mt-3">Product interface preview — Dashboard, Finance, HR, Attendance, Payroll, Expenses, Invoices & Reports</p>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-slate-900 text-white" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <p className="text-center text-sm text-slate-400 mb-6">{t.trust_strip_text}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {(trustItems.length ? trustItems : [{ label: "Secure Cloud Platform" }, { label: "Role-Based Access" }, { label: "Multi-Center Ready" }, { label: "GST-Ready Invoicing" }]).map((item) => (
              <div key={item.label} className="text-center">
                <p className="font-display font-semibold text-white">{item.label}</p>
                {item.value && <p className="text-emerald-400 text-sm mt-1">{item.value}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-16 sm:py-24" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-red-600 mb-3">The Problem</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              {t.problem_h2}
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PROBLEMS.map((p) => (
              <div key={p} className="flex items-start gap-3 bg-red-50/60 border border-red-100 rounded-xl p-4">
                <X className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">{p}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <p className="font-display text-xl font-bold text-slate-900">There's a better way.</p>
            <Button data-testid="problem-cta-button" onClick={() => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" })}
              variant="outline" className="rounded-xl border-emerald-700 text-emerald-700 hover:bg-emerald-50">
              See How Mashara Finance Works
            </Button>
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="py-16 sm:py-24 bg-slate-50" id="platform" data-testid="solution-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-3">The Platform</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              {t.platform_h2}
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">{t.platform_sub}</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Object.entries(MODULES).map(([key, m]) => (
              <div key={key} data-testid={`feature-card-${key}`} className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all duration-300">
                <h3 className="font-display font-semibold text-lg text-slate-900">
                  {MODULE_LINKS[key] ? (
                    <Link to={MODULE_LINKS[key]} data-testid={`feature-link-${key}`} className="hover:text-emerald-700 transition-colors">{m.name}</Link>
                  ) : m.name}
                </h3>
                <p className="mt-3 text-sm text-slate-500"><span className="font-medium text-red-600">Problem: </span>{m.problem}</p>
                <p className="mt-2 text-sm text-slate-700"><span className="font-medium text-emerald-700">Benefit: </span>{m.benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEMO */}
      <section className="py-16 sm:py-24" id="demo" data-testid="demo-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-3">Product Tour</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              {t.demo_h2}
            </h2>
          </div>
          <Tabs value={demoTab} onValueChange={(v) => { setDemoTab(v); track("demo_tab_click", { tab: v }); }}>
            <TabsList data-testid="demo-tabs" className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1 rounded-xl mb-8">
              {DEMO_TABS.map((t) => (
                <TabsTrigger key={t} value={t} data-testid={`demo-tab-${t}`} className="rounded-lg capitalize data-[state=active]:bg-emerald-700 data-[state=active]:text-white">
                  {t === "hr" ? "HR" : t}
                </TabsTrigger>
              ))}
            </TabsList>
            {DEMO_TABS.map((t) => (
              <TabsContent key={t} value={t}>
                <ProductMockup active={t} />
              </TabsContent>
            ))}
          </Tabs>
          <div className="mt-8 text-center">
            <Button data-testid="demo-cta-button" size="lg" onClick={() => { track("hero_cta_click", { location: "demo" }); navigate("/signup"); }}
              className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white px-8">
              Start Using Mashara Finance <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* SEGMENTS */}
      <section className="py-16 sm:py-24 bg-slate-50" id="segments" data-testid="segments-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-3">Who It's For</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              {t.segments_h2}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SEGMENTS.map((s) => {
              const Icon = ICONS[s.icon];
              const hasPage = SEGMENT_PAGES.includes(s.slug);
              return (
                <div key={s.slug} data-testid={`segment-${s.slug}`} className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-display font-semibold text-lg text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 flex-1">{s.desc}</p>
                  <Link to={hasPage ? `/solutions/${s.slug}` : "/demo"} data-testid={`segment-cta-${s.slug}`}
                    className="mt-4 text-sm font-semibold text-emerald-700 hover:text-emerald-800 flex items-center gap-1">
                    {hasPage ? "Explore Solution" : "Talk to Us"} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-16 sm:py-24" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-3">Outcomes</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              {t.benefits_h2}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {BENEFITS.map((b) => (
              <div key={b.title} className="rounded-xl border border-slate-200 bg-white p-5">
                <Check className="w-5 h-5 text-emerald-600 mb-3" />
                <h3 className="font-semibold text-slate-900 text-sm">{b.title}</h3>
                <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="py-16 sm:py-24 bg-slate-50" data-testid="before-after-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 text-center max-w-3xl mx-auto">
            {t.before_after_h2}
          </h2>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-red-200 bg-white p-8">
              <p className="text-xs font-semibold uppercase tracking-widest text-red-600 mb-6">Before Mashara Finance</p>
              <ul className="space-y-4">
                {BEFORE_AFTER.before.map((b) => (
                  <li key={b} className="flex items-center gap-3 text-slate-700"><X className="w-5 h-5 text-red-500 shrink-0" /> {b}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-8">
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-6">After Mashara Finance</p>
              <ul className="space-y-4">
                {BEFORE_AFTER.after.map((a) => (
                  <li key={a} className="flex items-center gap-3 text-slate-800 font-medium"><Check className="w-5 h-5 text-emerald-600 shrink-0" /> {a}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="py-16 sm:py-24" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-3">Customer Stories</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              {t.testimonials_h2}
            </h2>
          </div>
          {testimonials.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="testimonials-placeholder">
              <p className="text-slate-600 font-medium">{t.testimonials_empty}</p>
              <p className="text-sm text-slate-500 mt-2">We publish only verified testimonials with specific outcomes — no filler quotes. Be our next success story.</p>
              <Button data-testid="testimonial-cta-button" className="mt-6 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => navigate("/signup")}>
                Start Your Free Trial
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <figure key={i} data-testid={`testimonial-${i}`} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <blockquote className="text-sm text-slate-700 leading-relaxed">"{t.quote}"</blockquote>
                  <figcaption className="mt-4">
                    <p className="font-semibold text-slate-900 text-sm">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.designation}, {t.organization}</p>
                    {t.outcome && <p className="text-xs text-emerald-700 mt-1 font-medium">{t.outcome}</p>}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SECURITY */}
      <section className="py-16 sm:py-24 bg-slate-900 text-white" id="security" data-testid="security-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-400 mb-3">Security & Trust</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              {t.security_h2}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Lock, title: "Secure Login", desc: "Encrypted password storage and secure session handling." },
              { icon: KeyRound, title: "Role-Based Access", desc: "Control exactly who can view or edit each module." },
              { icon: FileCheck, title: "Audit-Friendly Records", desc: "Approvals and payroll runs are recorded and traceable." },
              { icon: ShieldCheck, title: "Secure Payments", desc: "Payments processed by Razorpay — we never store card details." },
              { icon: DatabaseBackup, title: "Cloud Backup", desc: "Your data is hosted securely with regular backups." },
              { icon: Users, title: "Permission Management", desc: "Plan-based entitlements enforced on the server, not the browser." },
            ].map((s) => (
              <div key={s.title} className="rounded-2xl border border-slate-800 bg-slate-800/50 p-6">
                <s.icon className="w-6 h-6 text-emerald-400 mb-3" />
                <h3 className="font-semibold">{s.title}</h3>
                <p className="mt-1.5 text-sm text-slate-400">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      {faqs.length > 0 && (
        <section className="py-16 sm:py-24" data-testid="faq-preview-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-8 text-center">
              {t.faq_h2}
            </h2>
            <Accordion type="single" collapsible>
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-slate-900 font-medium">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="text-center mt-6">
              <Link to="/faq" data-testid="faq-view-all-link" className="text-sm font-semibold text-emerald-700 hover:text-emerald-800">
                View all questions →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-16 sm:py-24" data-testid="final-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-r from-emerald-900 via-slate-900 to-emerald-950 text-white p-10 sm:p-16 text-center">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              {t.final_h2}
            </h2>
            <p className="mt-4 text-slate-300 max-w-2xl mx-auto">{t.final_sub}</p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <Button data-testid="final-trial-button" size="lg" onClick={() => { track("hero_cta_click", { location: "final_cta" }); navigate("/signup"); }}
                className="rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-6 text-base font-semibold">
                Start Free Trial
              </Button>
              <Button data-testid="final-demo-button" size="lg" variant="outline" onClick={() => navigate("/demo")}
                className="rounded-xl border-slate-500 text-white hover:bg-white/10 px-8 py-6 text-base font-semibold bg-transparent">
                Book a Demo
              </Button>
            </div>
            <p className="mt-4 text-xs text-slate-400">10-day free trial · Activated with ₹1 verification · Cancel anytime</p>
          </div>
        </div>
      </section>
    </div>
  );
}
